/**
 * Pure URL builders for the grid → detail → back chain. Lives here so the
 * round-trip can be unit-tested without rendering React.
 */

export interface BuildDetailHrefInput {
  id: number;
  /** Set descriptor (photos, videos, tag:<slug>, playlist:<id>, favorites, …). */
  setParam: string;
  /** Add lightbox=1 when true. */
  lightbox: boolean;
  /**
   * Extra query params to carry into the detail URL. Keys with undefined or
   * null values are skipped (callers may pass `seed` only in random mode, for
   * instance). Insertion order is preserved in the output to keep snapshots
   * stable.
   */
  extra?: Record<string, string | null | undefined>;
}

export function buildDetailHref(input: BuildDetailHrefInput): string {
  const parts: string[] = [`set=${encodeURIComponent(input.setParam)}`];
  if (input.lightbox) parts.push("lightbox=1");
  if (input.extra) {
    for (const [k, v] of Object.entries(input.extra)) {
      if (v == null) continue;
      parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
    }
  }
  return `/media/${input.id}?${parts.join("&")}`;
}

export interface BuildBackHrefInput {
  /**
   * Set descriptor (as it was passed to MediaDetail). Determines the base
   * page; e.g. "photos" → /photos, "tag:cats" → /tags/cats,
   * "playlist:42" → /playlists/42.
   */
  set: string;
  /** Encoded structured filter (the `filter` URL param). */
  filter?: string | null;
  /** Grid sort order. */
  order?: string | null;
  /** Chip-filter include slugs. */
  includeTagSlugs?: readonly string[];
  /** Chip-filter exclude slugs. */
  excludeTagSlugs?: readonly string[];
}

export function buildBackHref(input: BuildBackHrefInput): string {
  let base: string;
  if (input.set === "photos") base = "/photos";
  else if (input.set === "videos") base = "/videos";
  else if (input.set === "favorites") base = "/favorites";
  else if (input.set.startsWith("tag:")) base = `/tags/${encodeURIComponent(input.set.slice(4))}`;
  else if (input.set.startsWith("playlist:")) base = `/playlists/${input.set.slice("playlist:".length)}`;
  else base = "/";

  const params = new URLSearchParams();
  if (input.filter) params.set("filter", input.filter);
  if (input.order) params.set("order", input.order);
  if (input.includeTagSlugs && input.includeTagSlugs.length > 0)
    params.set("tags", input.includeTagSlugs.join(","));
  if (input.excludeTagSlugs && input.excludeTagSlugs.length > 0)
    params.set("excludeTags", input.excludeTagSlugs.join(","));
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/**
 * Build a "play" launch link into the immersive detail view.
 *
 * `next=display` is always appended so a saved `mv-next-mode=folder`
 * preference can't redirect the sequence out of the chosen set. For shuffle,
 * `order` is intentionally dropped — random playback ignores sort and an
 * `order=random` link would otherwise need a seed.
 */
export function buildPlayHref(input: {
  id: number;
  /** Set descriptor (photos, videos, tag:<slug>, playlist:<id>, favorites, all). */
  set: string;
  /** When true, start endless random play. */
  shuffle?: boolean;
  /** Grid sort; included only for ordered play and only when not the default "newest". */
  order?: string | null;
  /** Random-shuffle seed; required by the neighbors endpoint when order is "random". */
  seed?: string | null;
  /** Encoded structured-search filter; carried so play-all stays inside the filtered set. */
  filter?: string | null;
}): string {
  const parts: string[] = [`set=${encodeURIComponent(input.set)}`, "auto=playall"];
  if (input.shuffle) {
    parts.push("shuffle=1");
  } else {
    if (input.order && input.order !== "newest") {
      parts.push(`order=${encodeURIComponent(input.order)}`);
      // Random order paginates off a stable seed; without it the neighbors
      // endpoint 400s (seed_required_for_random) and auto-advance stalls on
      // the first item.
      if (input.order === "random" && input.seed) {
        parts.push(`seed=${encodeURIComponent(input.seed)}`);
      }
    }
    if (input.filter) {
      parts.push(`filter=${encodeURIComponent(input.filter)}`);
    }
  }
  parts.push("next=display");
  return `/media/${input.id}?${parts.join("&")}`;
}
